import PropTypes from "prop-types";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import SaleInvoicesTable from "./SaleInvoicesTable";

// MUI
import { CELL_FONT_SIZE, FOOTER_CELL_FONT_SIZE, TABLE_ROWS, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { Tooltip, Box } from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

//Helpers
import { isFooterCell } from "@/Common/helpers/Tables";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

//Aggregations
import { tableKeys } from "@/Constants/tableKeys";
import useAggregationExtended from "@/Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";

// Constants
import { columnProps } from "@/Common/constants/tableColumnProps";
import { computeRowExpansionPanelHeight } from "@/Common/utils/TableUtils";
import { DATE_FORMAT } from "@/Constants/date";

import { useCallback, useMemo } from "react";

const getInvalidTooltip = ({ isValidEmployee, isValidChargeback }) => {
	const isInvalidCode = isValidChargeback === false, isInvalidEmployee = isValidEmployee === false;
	if (isInvalidCode && isInvalidEmployee) {
		return "Invalid chargeback codes and employees";
	}
	if (isInvalidEmployee) {
		return "Invalid Employees";
	}
	if (isInvalidCode) {
		return "Invalid chargeback codes";
	}
	return null;
};

const isRowInvalid = (row) => row?.isValidChargeback === false || row?.isValidEmployee === false;

export default function ChargebacksTable({ data = [], filters = [], onFilterModelChange }) {
	const apiRef = useGridApiRef();

	const expandedRowIds = useMemo(() => {
		const expandInvalid	= filters.some((filter) => filter.field === "invalid");
		if (!expandInvalid) {
			return undefined;
		}
		return data
			.filter(isRowInvalid)
			.map((row) => row?._id)
			.filter(Boolean);
	}, [data, filters]);

	const aggregations = useAggregationExtended({
		uniqueCount: true,
		avgNumberBetweenDates: { data: data.map((item) => item.chargebackDate) || [] },
	});

	const columns = [
		{ field: "_id", headerName: "ID", width: 10, hide: true },
		{
			field: "invalid",
			headerName: "",
			width: 48,
			valueGetter: (_, row) => isRowInvalid(row),
			renderCell: (params) => {
				const tooltip = getInvalidTooltip(params.row ?? {});
				if (!tooltip) {
					return null;
				}
				return (
					<Box display="flex" alignItems="center" justifyContent="center">
						<Tooltip title={tooltip} placement="right" arrow>
							<ErrorOutlineRoundedIcon
								sx={{
									color: "#d32f2f",
									fontSize: "1.25rem",
									cursor: "pointer",
								}}
							/>
						</Tooltip>
					</Box>
				);
			},
		},
		{
			field: "saleDate",
			headerName: "Sale Date",
			type: "date",
			...columnProps,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderCell: (params) => {
				if (params.row?.emptyRow) {
					return null;
				}
				if (!!params.row?.dateOutOfRange) {
					return <CustomCell value="Date out of range" fontSize={CELL_FONT_SIZE} />;
				}
				return isFooterCell(params) ? (
					<TypographyCell hints={{ isFooter: true, noWrap: true, noFocus: true }} fontSize={FOOTER_CELL_FONT_SIZE}>
						Totals
					</TypographyCell>
				) : (
					<CustomCell value={params.formattedValue} hasTooltip fontSize={CELL_FONT_SIZE} />
				);
			},
		},
		{
			field: "saleGross",
			headerName: "Sale Gross",
			align: "right",
			type: "number",
			...columnProps,
			renderCell: (params) => {
				const isFooter = isFooterCell(params);
				return (
					<TypographyCell
						render="absolute"
						params={params}
						hints={{ isMoney: true, noFocus: true }}
						fontSize={isFooter ? FOOTER_CELL_FONT_SIZE : CELL_FONT_SIZE}
					/>
				);
			},
		},
		{ field: "saleType", headerName: "Sale Type", ...columnProps },
		{
			field: "vehicleMake",
			headerName: "Make",
			...columnProps,
			valueGetter: (_, row) => row.vehicleInformation?.make,
		},
		{
			field: "vehicleModel",
			headerName: "Model",
			...columnProps,
			valueGetter: (_, row) => row.vehicleInformation?.model,
		},
		{
			field: "vehicleModelYear",
			headerName: "Year",
			...columnProps,
			maxWidth: 80,
			valueGetter: (_, row) => row.vehicleInformation?.modelYear,
		},
		{ field: "dealNumber", headerName: "Deal Number", ...columnProps },
		{ field: "stockNumber", headerName: "Stock Number", ...columnProps },
		{ field: "customerNumber", headerName: "Customer Number", ...columnProps },
	];

	const getInvoiceTable = useCallback(({ row }) => {
		const invoices = row.invoices?.map((invoice) => ({ ...row, ...invoice }));
		return (
			<div className="pt-1 pl-5 pr-10">
				<SaleInvoicesTable columnProps={columnProps} data={invoices} />
			</div>
		);
	}, []);

	return (
		<DataSmartTable
			filterStoreConfig={{ key: tableKeys.DATA_PAGES.CHARGE_BACKS }}
			apiRef={apiRef}
			columns={columns}
			rows={data}
			maxRows={TABLE_ROWS}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={{ _id: false }}
			onFilterModelChange={onFilterModelChange}
			initialState={{
				filter: {
					filterModel: {
						items: filters,
						logicOperator: "and",
					},
				},
				aggregation: {
					model: {
						saleGross: "avg",
						saleDate: "avgNumberBetweenDates",
					},
				},
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					filter: { hide: false },
					search: { useQuickFilter: true },
				},
				noRowsOverlay: { title: "No Chargeback Data" },
			}}
			hideFooter
			enableRowsFiller
			customProps={{
				tableClassName: {
					"functional-table footer-table-included": true,
					...TAILWIND_FOR_DATA_SMART_TABLE,
					"[&_.toolbar-actions]:!top-[-10px]": true,
					"[&_.full-collapse-toolbar]:!h-[33px]": true,
					"[&_.MuiDataGrid-main]:!mt-[16px]": true,
				},
				aggregationFooterClassName: "aggregation-footer regular-footer",
				hide: false,
				customAggregationsFn: { ...aggregations },
				enableRowExpansion: true,
				expandedRowIds,
				getRowExpansionContent: getInvoiceTable,
				rowExpansionPanelHeight: ({ row }) => computeRowExpansionPanelHeight(row.invoices?.length),
			}}
		/>
	);
}

ChargebacksTable.propTypes = {
	data: PropTypes.array,
	filters: PropTypes.array,
	onFilterModelChange: PropTypes.func,
};
